import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  MessageCircle, 
  Bell, 
  TrendingUp, 
  FolderOpen,
  Check,
  Star,
  Shield,
  Lock,
  Database,
  Clock,
  Award,
  Menu,
  X,
  GraduationCap,
  Users,
  Zap,
  School,
  Heart,
  Trophy,
  Gift,
  Medal,
  Target
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import LanguageSelector from '../components/LanguageSelector'

export default function LandingPage() {
  const { t } = useTranslation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Dashboard Features - 4 cards with soft colored backgrounds
  const dashboardFeatures = [
    {
      icon: MessageCircle,
      title: t('common.messages'),
      description: t('landing.messagesDesc'),
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600'
    },
    {
      icon: Bell,
      title: t('common.announcements'),
      description: t('landing.announcementsDesc'),
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      icon: TrendingUp,
      title: t('landing.studentProgress'),
      description: t('landing.progressDesc'),
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    {
      icon: FolderOpen,
      title: t('landing.pointsPortfolio'),
      description: t('landing.portfolioDesc'),
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600'
    }
  ]

  // Why Schools Love - 4 key benefits
  const whySchoolsLove = [
    {
      icon: Zap,
      title: 'Easy Communication',
      description: 'Connect teachers, parents, and students instantly with real-time messaging'
    },
    {
      icon: TrendingUp,
      title: 'Student Progress Tracking',
      description: 'Monitor academic performance and celebrate achievements'
    },
    {
      icon: Shield,
      title: 'Private & Secure',
      description: 'Enterprise-grade security with GDPR and POPIA compliance'
    },
    {
      icon: School,
      title: 'Built for Schools',
      description: 'Designed specifically for educational institutions by educators'
    }
  ]

  // Designed for Everyone - 4 colored role cards
  const roles = [
    {
      title: 'Teachers',
      color: 'bg-teal-500',
      features: ['Manage classes & attendance', 'Award points & track progress', 'Message parents directly', 'Post announcements']
    },
    {
      title: 'Parents',
      color: 'bg-blue-500',
      features: ['View child progress', 'Message teachers', 'Get notifications', 'Make payments']
    },
    {
      title: 'Students',
      color: 'bg-purple-500',
      features: ['View points & achievements', 'Upload portfolio work', 'See announcements', 'Message teachers']
    },
    {
      title: 'Administrators',
      color: 'bg-orange-500',
      features: ['Manage all users', 'View analytics', 'Control subscriptions', 'School-wide settings']
    }
  ]

  // Achievement/Points visualization
  const achievements = [
    { icon: Trophy, label: 'Top Performer', color: 'text-yellow-500', bg: 'bg-yellow-50' },
    { icon: Medal, label: 'Helper Badge', color: 'text-teal-500', bg: 'bg-teal-50' },
    { icon: Star, label: 'Star Student', color: 'text-purple-500', bg: 'bg-purple-50' },
    { icon: Target, label: 'Goal Crusher', color: 'text-orange-500', bg: 'bg-orange-50' },
    { icon: Gift, label: 'Reward Earned', color: 'text-pink-500', bg: 'bg-pink-50' },
    { icon: Award, label: 'Achievement', color: 'text-blue-500', bg: 'bg-blue-50' }
  ]

  // Security features
  const securityFeatures = [
    { icon: Lock, title: 'Data Encryption', desc: 'End-to-end encryption for all data' },
    { icon: Shield, title: 'GDPR & POPIA Compliant', desc: 'Full compliance with data protection laws' },
    { icon: Users, title: 'Role-based Access', desc: 'Strict permissions for each user type' },
    { icon: Database, title: 'Automated Backups', desc: 'Daily backups with 30-day retention' },
    { icon: Award, title: 'SOC 2 Certified', desc: 'Enterprise-grade security standards' },
    { icon: Clock, title: 'Regular Security Audits', desc: 'Continuous monitoring and updates' }
  ]

  // Testimonials
  const testimonials = [
    {
      quote: "SchoolConnect Hub has transformed how we communicate with parents. It's intuitive and saves us hours every week.",
      author: "Sarah Johnson",
      role: "Principal, Sunshine Primary"
    },
    {
      quote: "As a parent, I finally feel connected to my child's education. The real-time updates are invaluable.",
      author: "Michael Chen",
      role: "Parent of 2 students"
    },
    {
      quote: "The points system motivates my students like nothing else. They love seeing their progress!",
      author: "Emily Rodriguez",
      role: "Grade 5 Teacher"
    }
  ]

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 font-heading">SchoolConnect</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">Features</a>
              <a href="#why-schools" className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">Why Schools</a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">Testimonials</a>
              <a href="#security" className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">Security</a>
            </nav>

            {/* Right side buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">
                Sign In
              </Link>
              <Link 
                to="/register-school" 
                className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
              >
                Start 14-Day Free Trial
              </Link>
              <LanguageSelector size="small" />
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block text-gray-600 py-2">Features</a>
              <a href="#why-schools" className="block text-gray-600 py-2">Why Schools</a>
              <a href="#testimonials" className="block text-gray-600 py-2">Testimonials</a>
              <Link to="/login" className="block text-gray-600 py-2">Sign In</Link>
              <Link to="/register-school" className="block bg-teal-600 text-white text-center py-3 rounded-full font-semibold">
                Start 14-Day Free Trial
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32 relative overflow-hidden bg-gradient-to-b from-sky-50 to-white">
        {/* Background decorative elements */}
        <div className="absolute top-20 right-0 w-1/2 h-full bg-gradient-to-bl from-sky-100/50 via-purple-50/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-50 rounded-full blur-3xl opacity-60" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left column - Text */}
            <div>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight font-heading">
                {t('landing.heroTitle')}
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-xl">
                {t('landing.heroSubtitle')}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link 
                  to="/register-school"
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors shadow-lg shadow-teal-600/20"
                >
                  Start 14-Day Free Trial
                </Link>
                <Link 
                  to="/login"
                  className="border-2 border-gray-200 hover:border-teal-600 text-gray-900 px-8 py-4 rounded-full font-semibold text-lg transition-colors"
                >
                  Sign In
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-500">14-day free trial. No credit card required. Cancel anytime.</p>
            </div>

            {/* Right column - App mockup */}
            <div className="relative">
              <div className="relative z-10 bg-white rounded-3xl shadow-2xl p-4 max-w-sm mx-auto border border-gray-100">
                <div className="bg-sky-50 rounded-2xl p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Sunshine Primary</p>
                      <p className="text-xs text-gray-500">Grade 5 - Mrs. Johnson</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Bell className="w-4 h-4 text-blue-500" />
                        <p className="text-sm font-medium text-gray-900">New Announcement</p>
                      </div>
                      <p className="text-xs text-gray-600 ml-6">Field trip permission slips due Friday</p>
                    </div>
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Trophy className="w-4 h-4 text-orange-500" />
                        <p className="text-sm font-medium text-gray-900">Points Earned!</p>
                      </div>
                      <p className="text-xs text-gray-600 ml-6">Emma received 50 points for helping</p>
                    </div>
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageCircle className="w-4 h-4 text-teal-500" />
                        <p className="text-sm font-medium text-gray-900">New Message</p>
                      </div>
                      <p className="text-xs text-gray-600 ml-6">Mrs. Smith: Great progress this week!</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative circles */}
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-teal-100 rounded-full opacity-60" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-purple-100 rounded-full opacity-60" />
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 font-heading">
              Your School Dashboard
            </h2>
            <p className="mt-4 text-gray-600 text-lg">
              Everything you need to manage your school community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dashboardFeatures.map((feature, idx) => (
              <div key={idx} className={`${feature.bgColor} rounded-2xl p-6 hover:shadow-lg transition-shadow border border-gray-100`}>
                <div className={`w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm`}>
                  <feature.icon className={`w-6 h-6 ${feature.iconColor}`} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Schools Love Section */}
      <section id="why-schools" className="py-20 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 font-heading">
              Why Schools Love SchoolConnect Hub
            </h2>
            <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
              Designed with input from educators, parents, and administrators to solve real communication challenges
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whySchoolsLove.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Designed for Everyone Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 font-heading">
              Designed for Everyone
            </h2>
            <p className="mt-4 text-gray-600 text-lg">
              Tailored experiences for every member of your school community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roles.map((role, idx) => (
              <div key={idx} className={`${role.color} rounded-2xl p-6 text-white`}>
                <h3 className="text-xl font-bold mb-4">{role.title}</h3>
                <ul className="space-y-2">
                  {role.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-start gap-2 text-sm text-white/90">
                      <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievement/Points Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 font-heading mb-6">
                Motivate Students with Points & Rewards
              </h2>
              <p className="text-gray-600 text-lg mb-6">
                Our gamified points system encourages positive behavior and academic achievement. Students love tracking their progress and earning recognition.
              </p>
              <div className="flex gap-8 mb-8">
                <div>
                  <p className="text-3xl font-bold text-teal-600">10K+</p>
                  <p className="text-sm text-gray-600">Points Awarded</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-purple-600">95%</p>
                  <p className="text-sm text-gray-600">Student Engagement</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-600">500+</p>
                  <p className="text-sm text-gray-600">Badges Earned</p>
                </div>
              </div>
              <Link 
                to="/register-school"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
              >
                Learn More About Points
              </Link>
            </div>

            {/* Right - Achievement Cards */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Celebrate Every Achievement</h3>
                <div className="grid grid-cols-3 gap-3">
                  {achievements.map((achievement, idx) => (
                    <div key={idx} className={`${achievement.bg} rounded-xl p-4 text-center hover:shadow-md transition-shadow`}>
                      <achievement.icon className={`w-8 h-8 ${achievement.color} mx-auto mb-2`} />
                      <p className="text-xs font-medium text-gray-700">{achievement.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 text-sm">Emma's Progress</span>
                    <span className="font-bold text-gray-900">75%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full" />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">250 points earned this month</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 font-heading">
              Your Data is Safe With Us
            </h2>
            <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
              Enterprise-grade security with privacy at the core. We protect your school community's data with industry-leading standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityFeatures.map((feature, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow border border-gray-100">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                  <feature.icon className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white font-heading mb-4">
            Start Your 14-Day Free Trial
          </h2>
          <p className="text-teal-100 mb-8">
            Experience the full platform free for 14 days. No credit card required.
          </p>

          {/* Demo buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Link to="/demo/teacher" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl px-6 py-3 text-white font-medium transition-colors border border-white/20">
              Demo as Teacher
            </Link>
            <Link to="/demo/parent" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl px-6 py-3 text-white font-medium transition-colors border border-white/20">
              Demo as Parent
            </Link>
            <Link to="/demo/student" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl px-6 py-3 text-white font-medium transition-colors border border-white/20">
              Demo as Student
            </Link>
            <Link to="/demo/admin" className="bg-purple-500/20 hover:bg-purple-500/30 backdrop-blur-sm rounded-xl px-6 py-3 text-white font-medium transition-colors border border-purple-500/30">
              Demo as Admin
            </Link>
          </div>

          <Link 
            to="/register-school"
            className="inline-block bg-white text-teal-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-colors"
          >
            Start 14-Day Free Trial
          </Link>
          <p className="mt-4 text-sm text-teal-200">Setup takes less than 5 minutes</p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 font-heading">
              Stronger Connections. Brighter Futures.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Logo column */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold font-heading">SchoolConnect</span>
              </div>
              <p className="text-gray-400 text-sm">
                Connecting schools, teachers, parents, and students for better education.
              </p>
            </div>

            {/* Product links */}
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#security" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
              </ul>
            </div>

            {/* Resources links */}
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Webinars</a></li>
              </ul>
            </div>

            {/* Company links */}
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2024 SchoolConnect Hub. All rights reserved.
            </p>
            <div className="flex gap-6 text-gray-500 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}